import React, { useEffect, useState } from 'react';
import { Button, Icon, Modal } from 'semantic-ui-react';
import PaymentMethodInfo from './PaymentMethodInfo';
import { PAYMENT_WHATSAPP_DISPLAY, PAYMENT_WHATSAPP_URL } from './paymentContactInfo';
import { PAYMENT_BANNER_VARIANTS } from './paymentBrand';
import './payments.css';

const DISMISS_KEY_PREFIX = 'stocky.paymentBanner.dismissedAt.';

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('es-AR');
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const due = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dueOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  return Math.round((dueOnly.getTime() - todayOnly.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Payment notice banner. `variant` ('warning' | 'critical' | 'overdue') and the
 * date always come from the caller — this component only renders the state it's
 * given, it never decides when a user should see one. Every variant shares one
 * layout; only the severity colours and the wording change.
 */
function PaymentNoticeBanner({ variant = 'warning', dueDate = null, employee = false, onShareProof }) {
  const isOverdue = variant === 'overdue';
  const palette = PAYMENT_BANNER_VARIANTS[variant] || PAYMENT_BANNER_VARIANTS.warning;
  const closable = variant !== 'critical' && !isOverdue;
  const dismissKey = `${DISMISS_KEY_PREFIX}${dueDate || 'none'}`;

  const [dismissed, setDismissed] = useState(false);
  const [howToPayOpen, setHowToPayOpen] = useState(false);

  useEffect(() => {
    if (!closable) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(sessionStorage.getItem(dismissKey) === 'true');
    } catch {
      setDismissed(false);
    }
  }, [dismissKey, closable]);

  if (closable && dismissed) {
    return null;
  }

  const formattedDate = formatDate(dueDate);
  const remainingDays = daysUntil(dueDate);

  // An employee is told about the account's payment, not "their" payment.
  const subject = employee ? 'El pago de la cuenta' : 'Tu pago';
  let title;
  if (isOverdue) {
    title = `¡Hola! ${employee ? 'El pago de la cuenta está pendiente' : 'Tu pago está pendiente'} · Venció el ${formattedDate}`;
  } else if (variant === 'critical') {
    title =
      remainingDays !== null
        ? `¡Hola! ${subject} vence en ${Math.max(remainingDays, 0)} ${Math.abs(remainingDays) === 1 ? 'día' : 'días'}`
        : `¡Hola! ${subject} está por vencer`;
  } else {
    title = `¡Hola! ${employee ? 'El próximo pago de la cuenta' : 'Tu próximo pago'} vence el ${formattedDate}`;
  }

  // Blocking is a manual decision, so the notice says access *may* be blocked, never that it happens by itself.
  const overdueConsequence = 'Si continúa pendiente, el acceso podrá bloquearse en los próximos días.';
  let description;
  if (employee) {
    description = isOverdue
      ? `Queríamos avisarte que el pago de la cuenta ya venció. Avisale a quien la administra para seguir usando MercuryCom sin interrupciones. ${overdueConsequence}`
      : 'Queríamos recordarte que se acerca el vencimiento del pago de la cuenta. Avisale a quien la administra para seguir usando MercuryCom sin interrupciones.';
  } else if (isOverdue) {
    description = `Queríamos avisarte que tu factura ya venció. Para seguir usando MercuryCom sin interrupciones, ponte al día con tu pago. ${overdueConsequence}`;
  } else {
    description =
      'Queríamos recordarte que se acerca el vencimiento de tu factura. Para seguir usando MercuryCom sin interrupciones, recuerda realizar tu pago a tiempo.';
  }

  const handleClose = () => {
    try {
      sessionStorage.setItem(dismissKey, 'true');
    } catch {
      // sessionStorage unavailable (private mode, etc.) — just dismiss for this render.
    }
    setDismissed(true);
  };

  const handleShareProof = () => {
    if (onShareProof) {
      onShareProof();
      return;
    }
    window.open(PAYMENT_WHATSAPP_URL, '_blank', 'noopener,noreferrer');
  };

  const tokens = {
    '--payment-alert-bg': palette.background,
    '--payment-alert-border': palette.border,
    '--payment-alert-accent': palette.accent,
    '--payment-alert-icon-bg': palette.iconBackground,
    '--payment-alert-icon': palette.iconColor,
    '--payment-alert-title': palette.titleColor,
  };

  return (
    <div
      className={`payment-banner payment-alert${closable ? ' payment-alert--closable' : ''}`}
      role="alert"
      style={tokens}
    >
      <div className="payment-alert__main">
        <span className="payment-alert__icon" aria-hidden="true">
          <Icon name={palette.icon} />
        </span>
        <div className="payment-alert__content">
          <div className="payment-alert__title">{title}</div>
          <div className="payment-alert__description">{description}</div>
          <div className="payment-alert__contact">
            WhatsApp de atención al cliente · {PAYMENT_WHATSAPP_DISPLAY}
          </div>
          <div className="payment-alert__thanks">¡Gracias por confiar en MercuryCom!</div>
        </div>
      </div>

      <div className="payment-alert__actions">
        <Button
          type="button"
          basic
          className="payment-alert__secondary"
          onClick={() => setHowToPayOpen(true)}
        >
          Cómo pagar
        </Button>
        <Button type="button" color="violet" onClick={handleShareProof}>
          Contactar por WhatsApp
        </Button>
      </div>

      {closable ? (
        <button
          type="button"
          onClick={handleClose}
          aria-label="Cerrar aviso de pago"
          className="payment-alert__close"
        >
          <Icon name="close" />
        </button>
      ) : null}

      <Modal open={howToPayOpen} onClose={() => setHowToPayOpen(false)} size="tiny" closeIcon>
        <Modal.Header>Contacto para coordinar el pago</Modal.Header>
        <Modal.Content>
          <PaymentMethodInfo />
        </Modal.Content>
      </Modal>
    </div>
  );
}

export default PaymentNoticeBanner;
